import axiosInstance from 'services/axiosInstance';
import { IResponse } from 'services/types';
import { CallScribeCallStatus } from 'services/callScribe/types';

/**
 * Mirrors `CallScribeDashboardCallRow` on the BE — slimmer than the call-list projection
 * because the dashboard never shows transcript / link state, only the row metadata
 * needed to render "Recent calls" and the quick-dial / top-customer lists.
 */
export interface ICallScribeDashboardCallRow {
  callId: number;
  techId: number;
  createdAtUtc: string;
  /** Numeric per the BE; the FE maps via CallScribeCallStatus for display. */
  status: CallScribeCallStatus;
  toNumber: string;
  companyExternalId: string | null;
  contactExternalId: string | null;
  durationSeconds: number | null;
}

/** Mirrors `CallScribeStatusBreakdownEntry`. */
export interface ICallScribeStatusBreakdownEntry {
  status: CallScribeCallStatus;
  count: number;
}

/** Mirrors `CallScribeSentimentTrendPoint`. */
export interface ICallScribeSentimentTrendPoint {
  dateUtc: string;
  positive: number;
  neutral: number;
  negative: number;
  mixed: number;
  unknown: number;
}

/** Mirrors `CallScribeTopCustomerEntry`. */
export interface ICallScribeTopCustomerEntry {
  companyExternalId: string;
  crmSourceId: number | null;
  callCount: number;
  totalDurationSeconds: number;
}

/** Mirrors `CallScribeQuickDialEntry`. */
export interface ICallScribeQuickDialEntry {
  toNumber: string;
  companyExternalId: string | null;
  contactExternalId: string | null;
  crmSourceId?: number | null;
  lastCalledAtUtc: string;
}

export interface ICallScribeEmotionCounts {
  satisfied: number;
  neutral: number;
  confused: number;
  anxious: number;
  frustrated: number;
  angry: number;
}

export interface ICallScribeMyWeek {
  calls: number;
  previousCalls: number;
  analysedCalls: number;
  escalations: number;
  previousEscalations: number;
  endingMix: ICallScribeEmotionCounts;
}

/**
 * Mirrors the BE `CallScribeMyDashboardDto`. Every collection is always present
 * (empty when no data) so consumers can bind without null checks.
 */
export interface ICallScribeMyDashboard {
  todayCallCount: number;
  todayTotalDurationSeconds: number;
  recentCalls: ICallScribeDashboardCallRow[];
  todayStatusBreakdown: ICallScribeStatusBreakdownEntry[];
  sentimentTrend7d: ICallScribeSentimentTrendPoint[];
  topCustomersThisWeek: ICallScribeTopCustomerEntry[];
  quickDialRecents: ICallScribeQuickDialEntry[];
  unfinishedFollowUpsCount: number;
  myWeek?: ICallScribeMyWeek;
}

/** GET /api/call-scribe/dashboard/me — per-tech dashboard projection. */
export const getCallScribeMyDashboard = (): IResponse<ICallScribeMyDashboard> =>
  axiosInstance.request({
    method: 'GET',
    url: 'call-scribe/dashboard/me',
  });
